import ChevronRight from "@assets/svg/ChevronRight";
import SearchIcon from "@assets/svg/SearchIcon";
import CallIcon from "@assets/svg/newCall";
import CustomText from "@components/CustomText";
import EmptySearchList from "@components/SearchHeader/EmptySearchList";
import { appEnv } from "@config";
import color from "@constants/color";
import { meta } from "@constants/cplp";
import { colors } from "@constants/theme";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import CrateItem from "@screens/CustomerOrderItems/components/CrateCard";
import ShipmentCard from "@screens/CustomerOrderItems/components/ShipmentCard";
import HeaderWithSearch from "@screens/MyDeliveries/HeaderWithSearch";
import AddToBagBtn from "@screens/MyDeliveries/components/AddToBagBtn";
import CrateGroup from "@screens/MyDeliveries/components/CrateGroup";
import MarkingInfoModal from "@screens/MyDeliveries/components/MarkingInfoModal";
import {
  getDeliveryOrderItems,
  itemsAddedInBag,
  markAction,
  markMyDeliveyModalAction,
} from "@services/api";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import toasts from "@utils/toast";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  SectionList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useSelector } from "react-redux";
import InfoModal from "../CustomerOrderItems/components/InfoModal";
import InfoLines from "../CustomerOrderItems/components/InfoModal/InfoLines";
import { checkCanMarkAllQuantity, checkIfAllDefective } from "./utils";

function getCrateOrShipmentItem(type) {
  return type === "CRATE" || type === "PACKED_ORDERS"
    ? CrateItem
    : ShipmentCard;
}

function ItemList({ navigation, route }) {
  const getDeliveryOrderItemsApi = getDeliveryOrderItems;

  const is_cmo_cp = useSelector(({ auth }) => auth?.user?.is_cmo_cp);
  const isFocused = useIsFocused();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ordersItem, setOrdersItem] = useState([]);
  const [fullOrdersItem, setFullOrdersItem] = useState([]);
  const [meteData, setMetaData] = useState({});
  const [markingConfig, setMarkingConfig] = useState([]);
  const [searchEnable, setSearchEnable] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showConfirmSubmitModal, setShowConfirmSubmitModal] = useState(false);
  const [infoItemState, setInfoItemState] = useState(null);
  const [buttonPress, setButtonPress] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [allowAddToBag, setAllowAddToBag] = useState(false);
  const [isOfdCp, setIsOfdCp] = useState(false);
  const [plasticBagCount, setPlasticBagCount] = useState(0);
  const [imagesMandatoryForSamasya, setImagesMandatoryForSamasya] = useState(
    {},
  );
  const searchRef = useRef("");
  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]),
  );

  const fetchData = async () => {
    try {
      const { data } = await getDeliveryOrderItemsApi({
        order_ids: route?.params?.orders_list,
        spoke_name: route?.params?.spokeName || "",
        user_id: route?.params?.user_id,
        date: route?.params?.date || "",
        source: "MY_DELIVERIES",
      });
      setIsLoading(false);
      if (searchEnable && searchRef.current)
        onSearchInputChange(searchRef.current ? searchRef.current : "");
      else setOrdersItem(data?.data);
      setMetaData(data?.meta);
      setMarkingConfig(data?.marking_config);
      setFullOrdersItem(data?.data);
      setShowConfirmSubmitModal(data?.show_confirmSubmitModal);
      setInfoItemState(data?.item_info_state);
      setShowSubmit(data?.showSubmitButton);
      setIsOfdCp(data?.is_ofd_cp);
      setAllowAddToBag(data?.is_issue_item_with_images);
      setImagesMandatoryForSamasya(data?.images_mandatory_for_samasya);
      setPlasticBagCount(data?.plastic_bag_count || 0);
    } catch (error) {
      console.log(error);
    }
    setIsRefreshing(false);
  };

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const sortUpdates = useMemo(() => {
    return ordersItem.map((v) => {
      if (is_cmo_cp && v?.type === "CRATE" && Array.isArray(v?.items)) {
        const order = [];
        const groups = new Map();
        v.items.forEach((it) => {
          const key = it?.crate_id || "__no_crate";
          if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
          }
          groups.get(key).push(it);
        });
        const data = order.map((key) => ({
          __row: "crate_group",
          crate_id: key === "__no_crate" ? null : key,
          items: groups.get(key),
        }));
        return { ...v, data };
      }
      return { ...v, data: v.items };
    });
  }, [ordersItem, is_cmo_cp]);

  const handleAddToBag = async () => {
    try {
      if (showConfirmSubmitModal && !checkCanMarkAllQuantity(fullOrdersItem)) {
        setVisible(true);
        return;
      }
      if (!allowAddToBag) {
        toasts.showShortToast("कृप्या फोटो डालें!");
        return;
      }
      const order_item_ids = [];
      ordersItem.forEach((v) => {
        if (v?.items && Array.isArray(v.items)) {
          v.items.forEach((i) => {
            if (i?.order_item_id) {
              order_item_ids.push(i.order_item_id);
            }
          });
        }
      });

      await itemsAddedInBag({ order_item_ids });
      navigation.goBack();
    } catch (error) {
      console.log(error);
    }
  };

  const renderItem = useCallback(
    ({ item, section }) => {
      if (item?.__row === "crate_group") {
        return (
          <CrateGroup
            crate_id={item.crate_id}
            items={item.items}
            markingConfig={markingConfig}
            fetchData={fetchData}
            imagesMandatoryForSamasya={imagesMandatoryForSamasya}
            isOfdCp={isOfdCp}
            forceExpanded={!!searchRef.current}
          />
        );
      }
      const itemBackgroundColor =
        (section?.type === "CRATE" || section?.type === "PACKED_ORDERS") &&
        !item?.is_combo
          ? "tranparent"
          : "white";
      const CrateOrShipmentItem = getCrateOrShipmentItem(section?.type);
      return (
        <View style={{ backgroundColor: itemBackgroundColor }}>
          <CrateOrShipmentItem
            info={item}
            markingConfig={markingConfig}
            fetchData={fetchData}
            imagesMandatoryForSamasya={imagesMandatoryForSamasya}
            isOfdCp={isOfdCp}
            type={section?.type}
          />
        </View>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesMandatoryForSamasya],
  );

  const renderSection = ({ section: { type, data, display_type } }) => {
    const sectionTitle =
      display_type === "CRATE"
        ? meta.crate_item_label
        : display_type === "SKU_WISE_ORDER_ITEMS"
          ? meta.sku_wise_order_items_label
          : display_type === "PACKED_ORDERS"
            ? meta.packed_orders
            : meta.shipment_label;
    return data.length ? (
      <>
        {route?.params?.route_name ? (
          <View style={styles.headerContainer}>
            <View style={styles.headerContent}>
              <View style={styles.avatarContainer}>
                <CustomText variant="subtitle_16" style={styles.avatarText}>
                  {route?.params?.route_name?.charAt(0).toUpperCase()}
                </CustomText>
              </View>
              <View>
                <CustomText
                  variant="subtitle_16"
                  style={{
                    color:
                      route?.params?.db_name === "UNASSIGN"
                        ? color.mediumBlacklightBlack
                        : color.black,
                  }}
                >
                  {route?.params?.route_name}
                </CustomText>
                <CustomText
                  variant="subtitle_16"
                  style={{
                    color:
                      route?.params?.db_name === "UNASSIGN"
                        ? color.mediumBlacklightBlack
                        : color.black,
                  }}
                >
                  {route?.params?.db_name}
                </CustomText>
              </View>
            </View>
            <View style={styles.bagsContainer}>
              {route?.params?.bags?.map((i, index) => (
                <CustomText
                  variant="heading_24"
                  style={
                    index === route?.params?.bags?.length - 1
                      ? styles.bagTextLast
                      : styles.bagText
                  }
                >
                  {index === route?.params?.bags?.length - 1 ? i : `${i}, `}
                </CustomText>
              ))}
            </View>
          </View>
        ) : null}

        <View style={styles.sectionContainer}>
          <CustomText variant="body_text_2" style={styles.sectionTitle}>
            {sectionTitle}
          </CustomText>
        </View>
      </>
    ) : null;
  };

  const keyExtractor = (item, index) =>
    item?.__row === "crate_group"
      ? `crate_group_${item.crate_id || "no_crate"}_${index}`
      : `${item.id}_${index}`;

  const seeOrders = () => {
    if (route?.params?.orders_list.length === 1) {
      navigation.navigate("OrderDetails", {
        order_id: route?.params?.orders_list[0],
      });
    } else {
      navigation.navigate("DailyOrderList", {
        user_id: route?.params?.user_id,
        user_name: route?.params?.user_name,
        date: route?.params?.date,
        type: route?.params?.type,
      });
    }
  };

  function SubHeadingComponent() {
    return (
      <TouchableOpacity testID="see_orders" onPress={seeOrders}>
        <View style={{ flexDirection: "row" }}>
          <CustomText
            variant="body_text_4"
            style={{
              color: color.lightBlack,
              textDecorationLine: "underline",
            }}
          >
            {"आर्डर देखें "}
          </CustomText>
          <ChevronRight
            pathFill="rgba(0, 0, 0, 0.4)"
            style={{ height: 14, width: 14, marginTop: 2, marginLeft: 4 }}
          />
        </View>
      </TouchableOpacity>
    );
  }

  function RightComponent() {
    return (
      <View style={{ flexDirection: "row" }}>
        {/* <View>
          <TouchableOpacity
            testID="user_phone"
            onPress={() => onPressCall(route?.params?.user_phone)}
          >
            <CallIcon strokeColor="#2195F2" />
          </TouchableOpacity>
        </View> */}
        <View style={{ marginLeft: 24 }}>
          <TouchableOpacity
            testID="search_enable"
            onPress={() => setSearchEnable(true)}
          >
            <SearchIcon height={20} width={20} />
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const onPressCall = (phone) => {
    handleCallButtonPress(phone, "CX_NUMBER_CLICKED", {
      type: "call",
      user_id: route?.params?.user_id,
      user_name: route?.params?.user_name,
      orders: route?.params?.orders_list,
    });
  };

  const onPressCrossIcon = () => {
    onSearchInputChange("");
    searchRef.current = "";
  };

  const onSearchInputChange = debounce((text) => {
    const filteredData = fullOrdersItem.map((data) => {
      return {
        ...data,
        items: data?.items?.filter((v) =>
          containText(text, v?.product_name_en),
        ),
      };
    });
    setOrdersItem(filteredData);
    searchRef.current = text;
  }, 500);

  const shouldShowModal = () => {
    return infoItemState === "MODAL";
  };

  const containText = (text, item) => {
    const lowerCasedText = text?.trim().toLowerCase();
    return item?.toLowerCase().includes(lowerCasedText);
  };

  const getListHeaderComponent = () => {
    return (
      <>
        {plasticBagCount > 0 && (
          <View style={styles.plasticBagBanner}>
            <CustomText variant="body_text_2" style={styles.plasticBagText}>
              {`${plasticBagCount} पॉली बैग ज़रूरी। कृपया इस ऑर्डर को पॉली बैग में डिलीवर करें।`}
            </CustomText>
          </View>
        )}
        <InfoLines
          info={meteData?.InfoModal}
          state={infoItemState}
          onPress={onPressCut}
        />
      </>
    );
  };

  const onPressCut = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    await markMyDeliveyModalAction("item_info_IN_SCREEN");
    await fetchData();
    setButtonPress(false);
  };

  const footerComponent = () => (
    <View style={{ height: 240, backgroundColor: "white" }} />
  );

  const checkItemDatailsEmpty = () => {
    return !sortUpdates.find((v) => v?.data?.length > 0);
  };

  const onSubmitConfirmModal = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    const { user_id = "", spokeName = "", date = "" } = route?.params;
    const action = `confirm_submit_modal_${user_id}_${spokeName}_${date}`;
    await markAction(action);
    fetchData();
    setVisible(false);
    setButtonPress(false);
  };

  const onSubmit = async () => {
    if (buttonPress) {
      return;
    }
    setButtonPress(true);
    await markMyDeliveyModalAction("item_info_IN_SCREEN");
    await fetchData();
    setButtonPress(false);
  };

  if (isLoading) {
    return (
      <View style={styles.loadingView}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <HeaderWithSearch
        headertitle={`${route?.params?.user_name} जी का ऑर्डर`}
        headerColor="#fff"
        hideShadow={false}
        isSearchEnabled={searchEnable}
        navigation={navigation}
        route={route}
        rightComponent={<RightComponent />}
        subHeadingComponent={
          appEnv === "production" ? (
            <View style={{ height: 15 }} />
          ) : (
            <SubHeadingComponent />
          )
        }
        onPressCrossIcon={onPressCrossIcon}
        onSearchInputChange={onSearchInputChange}
        searchPlaceholder="आइटम सर्च करें…"
      />
      {checkItemDatailsEmpty() ? (
        <EmptySearchList />
      ) : (
        <View style={{ flex: 1 }}>
          <SectionList
            renderSectionHeader={renderSection}
            ListHeaderComponent={getListHeaderComponent}
            keyExtractor={keyExtractor}
            contentContainerStyle={{ marginTop: 8, paddingBottom: 80 }}
            sections={sortUpdates}
            renderItem={renderItem}
            keyboardShouldPersistTaps="handled"
            ListFooterComponent={footerComponent}
            onRefresh={onRefresh}
            refreshing={isRefreshing}
          />
          {showSubmit ? (
            <View style={styles.addToBagBtnContainer}>
              <AddToBagBtn
                handleAddToBag={handleAddToBag}
                isAllDefective={checkIfAllDefective(ordersItem)}
              />
            </View>
          ) : null}
        </View>
      )}
      <InfoModal
        visible={shouldShowModal()}
        info={meteData?.InfoModal}
        onSubmit={onSubmit}
      />
      <MarkingInfoModal
        onSubmit={onSubmitConfirmModal}
        visible={visible}
        data={meteData?.ConfirmSubmitModal}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionContainer: {
    padding: 12,
    backgroundColor: "white",
    paddingLeft: 16,
  },
  sectionTitle: { color: "#000" },
  addToBagBtnContainer: {
    backgroundColor: "white",
    position: "absolute",
    paddingBottom: 10,
    bottom: 0,
    width: "100%",
    padding: 16,
  },
  avatarContainer: {
    height: 35,
    width: 35,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: colors.black,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: colors.white,
  },
  headerContainer: {
    backgroundColor: "#F4FAF5",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 16,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  bagsContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  plasticBagBanner: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    // eslint-disable-next-line react-native/no-color-literals
    backgroundColor: "#FFF9E6",
    margin: 8,
    borderRadius: 6,
  },
  // eslint-disable-next-line react-native/no-color-literals
  plasticBagText: { color: "#B45309" },
});

export default ItemList;
